/* JAQUE HUÉ: still renders and live 3D viewers for jewelry sections. Requires snippets/cj-loader.liquid. */

export const DEFAULTS = {
  setting: 'solitaire', shape: 'Round', carat: '1.5', material: 'silver', color: 'white', width: 'classic',
  size: '7', stone: '3', length: '7', chain: '18', nlength: '16', bandw: '3', bsize: 'M', dia: '20', hstone: '2',
};

const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Wraps an angle into the range -PI..PI. */
const wrap = (a) => {
  a = (a + Math.PI) % (Math.PI * 2);
  if (a < 0) a += Math.PI * 2;
  return a - Math.PI;
};

/**
 * Mounts an interactive, auto-rotating viewer on a canvas.
 * @param {HTMLCanvasElement} canvas
 * @param {{kind: string, c: object, view?: string, spin?: boolean}} spec
 */
const COARSE = matchMedia('(pointer: coarse)').matches;
export function mountViewer(canvas, spec) {
  const X = window.CJ3D;
  const engine = X.Engine(canvas, false);
  const s = { kind: spec.kind, c: Object.assign({}, DEFAULTS, spec.c), key: '', rx: 0, ry: 0, tx: null, ty: null,
    drag: false, idle: 0, lx: 0, ly: 0, spin: spec.spin !== false && !REDUCE, visible: true, raf: 0, last: 0, zoom: 1, cx: 0, cy: 0 };
  const MAX_ZOOM = 4, pointers = new Map();
  let pinch = null;
  /** Zooms to nz while keeping the point under (nx, ny) (-1..1, y up) fixed on screen. */
  const zoomAt = (nz, nx, ny) => {
    nz = Math.max(1, Math.min(MAX_ZOOM, nz));
    const a = canvas.clientWidth / canvas.clientHeight || 1, lim = 1 - 1 / nz;
    s.cx += nx * a * (1 / s.zoom - 1 / nz);
    s.cy += ny * (1 / s.zoom - 1 / nz);
    s.zoom = nz;
    s.cx = Math.max(-lim * a, Math.min(lim * a, s.cx));
    s.cy = Math.max(-lim, Math.min(lim, s.cy));
    s.tz = 0;
    if (nz === 1) { s.cx = 0; s.cy = 0; }
    canvas.style.touchAction = nz > 1 ? 'none' : '';
    s.idle = performance.now();
    if (ctl.onZoom) ctl.onZoom(s.zoom);
  };
  const ndc = (x, y) => { const r = canvas.getBoundingClientRect(); return [((x - r.left) / r.width) * 2 - 1, 1 - ((y - r.top) / r.height) * 2]; };
  if (spec.zoom) {
    canvas.addEventListener('wheel', (e) => {
      // Let the page scroll when the piece is already fully zoomed out.
      if (s.zoom === 1 && e.deltaY > 0 && !e.ctrlKey) return;
      e.preventDefault();
      const [nx, ny] = ndc(e.clientX, e.clientY);
      zoomAt(s.zoom * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015)), nx, ny);
    }, { passive: false });
    canvas.addEventListener('dblclick', (e) => {
      const [nx, ny] = ndc(e.clientX, e.clientY);
      zoomAt(s.zoom > 1.2 ? 1 : 2.5, nx, ny);
    });
  }
  const v0 = X.VIEWS[s.kind][spec.view || 'angled'];
  s.ry = v0[0];
  s.rx = v0[1];

  canvas.addEventListener('pointerdown', (e) => {
    pointers.set(e.pointerId, [e.clientX, e.clientY]);
    if (spec.zoom && pointers.size === 2) {
      const [p, q] = [...pointers.values()];
      pinch = { d0: Math.hypot(p[0] - q[0], p[1] - q[1]) || 1, z0: s.zoom };
      s.drag = false;
      return;
    }
    s.drag = true; s.lx = e.clientX; s.ly = e.clientY; s.tx = null;
    s.gy = s.ry; s.gx = s.rx; s.vy = 0; s.vx = 0; s.lt = performance.now();
    try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
  });
  canvas.addEventListener('pointermove', (e) => {
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, [e.clientX, e.clientY]);
    if (pinch && pointers.size === 2) {
      const [p, q] = [...pointers.values()];
      const [nx, ny] = ndc((p[0] + q[0]) / 2, (p[1] + q[1]) / 2);
      zoomAt(pinch.z0 * Math.hypot(p[0] - q[0], p[1] - q[1]) / pinch.d0, nx, ny);
      return;
    }
    if (!s.drag) return;
    // use every touch sample the browser coalesced into this event, so fast swipes don't skip
    const evs = (e.getCoalescedEvents && e.getCoalescedEvents().length) ? e.getCoalescedEvents() : [e];
    for (const ev of evs) {
      const ddy = (ev.clientX - s.lx) * 0.01, ddx = (ev.clientY - s.ly) * 0.008;
      s.gy += ddy; s.gx = Math.max(-1.5, Math.min(1.5, s.gx + ddx));
      s.lx = ev.clientX; s.ly = ev.clientY;
    }
    const t = performance.now(), dtm = Math.max(1, t - s.lt);
    s.vy = s.vy * 0.6 + ((s.gy - (s.pgy ?? s.gy)) / dtm) * 0.4; s.vx = s.vx * 0.6 + ((s.gx - (s.pgx ?? s.gx)) / dtm) * 0.4;
    s.pgy = s.gy; s.pgx = s.gx; s.lt = t;
  });
  const up = (e) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = null;
    if (s.drag && performance.now() - s.lt > 80) { s.vy = 0; s.vx = 0; } // finger rested before lifting: no fling
    s.drag = false; s.pgy = s.pgx = undefined; s.idle = performance.now();
  };
  canvas.addEventListener('pointerup', up);
  canvas.addEventListener('pointercancel', up);

  s.pr = COARSE ? Math.min(window.devicePixelRatio || 1, 2.25) : Math.min(window.devicePixelRatio || 1, 2);
  if (COARSE) engine.pixelRatio(s.pr);
  const resize = () => {
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (w && h) engine.size(w, h);
  };
  new ResizeObserver(resize).observe(canvas);
  resize();

  new IntersectionObserver((entries) => {
    s.visible = entries[0].isIntersecting;
    if ((s.visible || s.keep) && !s.raf) s.raf = requestAnimationFrame(tick);
  }).observe(canvas);

  // the camera glides to the requested zoom/target instead of jumping (wheel, pinch, buttons, slider, views)
  const cam = { zoom: 1, cx: 0, cy: 0, tz: 0 };
  function draw(dt) {
    const key = s.kind + JSON.stringify(s.c);
    if (key !== s.key) { s.key = key; s.stats = engine.show(s.kind, s.c); if (ctl.onStats) ctl.onStats(s.stats); }
    const k = REDUCE || !dt ? 1 : 1 - Math.exp(-dt * (pinch ? 22 : 9));
    cam.zoom += (s.zoom - cam.zoom) * k; cam.cx += (s.cx - cam.cx) * k; cam.cy += (s.cy - cam.cy) * k; cam.tz += ((s.tz || 0) - cam.tz) * k;
    if (Math.abs(s.zoom - cam.zoom) < 0.0005) cam.zoom = s.zoom;
    engine.draw(s.rx, s.ry, cam);
    if (ctl.onFrame) ctl.onFrame(canvas);
  }
  function tick(now) {
    s.raf = 0;
    if ((!s.visible && !s.keep) || !canvas.isConnected) return;
    s.raf = requestAnimationFrame(tick);
    const dt = Math.min(0.05, (now - (s.last || now)) / 1000);
    s.last = now;
    if (s.tx !== null) {
      const dy = wrap(s.ty - s.ry), dx = s.tx - s.rx;
      s.ry += dy * 0.12; s.rx += dx * 0.12;
      if (Math.abs(dy) < 0.002 && Math.abs(dx) < 0.002) { s.tx = null; s.idle = now; }
    } else if (s.drag) {
      // ease toward the finger (frame-rate independent)
      const k = 1 - Math.exp(-dt * 28);
      s.ry += (s.gy - s.ry) * k; s.rx += (s.gx - s.rx) * k;
    } else if (Math.abs(s.vy || 0) > 0.00002 || Math.abs(s.vx || 0) > 0.00002) {
      // momentum after a flick, slowing down smoothly
      const ms = dt * 1000, fr = Math.exp(-dt * 4.5);
      s.ry += s.vy * ms; s.rx = Math.max(-1.5, Math.min(1.5, s.rx + s.vx * ms));
      s.vy *= fr; s.vx *= fr; s.idle = now;
    } else if (s.spin && s.zoom === 1 && now - s.idle > 1500) s.ry += dt * 0.35;
    // phones: if frames run slow, render a little sharper-than-1x instead of full retina to keep motion fluid
    if (COARSE && dt > 0) {
      s.ft = (s.ft || 16) * 0.92 + dt * 1000 * 0.08; s.fn = (s.fn || 0) + 1;
      if (s.fn > 45 && s.ft > 22 && s.pr > 1.51) { s.pr = Math.max(1.5, s.pr - 0.25); engine.pixelRatio(s.pr); resize(); s.fn = 0; }
    }
    draw(dt);
  }
  s.raf = requestAnimationFrame(tick);

  const ctl = {
    onStats: null,
    get stats() { return s.stats; },
    set(kind, c) { s.kind = kind; s.c = Object.assign({}, DEFAULTS, c); },
    // a view may carry its own zoom and target ([turn, tilt, zoom, cx, cy]), e.g. the clasp close-up
    view(name) { const v = X.VIEWS[s.kind][name]; if (v) { s.ty = v[0]; s.tx = v[1]; zoomAt(1, 0, 0); if (v[2]) { const tg = v[3] === 'clasp' ? engine.target('clasp', v[1], v[0]) : null; s.zoom = v[2]; s.cx = tg ? tg.cx : v[3] || 0; s.cy = tg ? tg.cy : v[4] || 0; s.tz = tg ? tg.tz : 0; } } },
    onZoom: null,
    onFrame: null,
    /** Keeps drawing while the canvas is off screen (for a mirror that copies its frames). */
    keepAlive(on) { s.keep = on; if (on && !s.raf) s.raf = requestAnimationFrame(tick); },
    get zoom() { return s.zoom; },
    /** Zoom toward a named part of the piece (e.g. 'zoomAim'); false when the piece has none. */
    zoomOn(z, name) {
      const nz = Math.max(1, Math.min(MAX_ZOOM, z)); if (nz === 1) { zoomAt(1, 0, 0); return true; }
      const tg = engine.target && engine.target(name, s.rx, s.ry); if (!tg) return false;
      const k = 1 - 1 / (nz * nz); s.zoom = nz; s.cx = tg.cx * k; s.cy = tg.cy * k; s.tz = tg.tz * k;
      canvas.style.touchAction = 'none'; s.idle = performance.now(); if (ctl.onZoom) ctl.onZoom(s.zoom); return true;
    },
    /** Zoom buttons: multiply the zoom, centred on (nx, ny). */
    zoomBy(f, nx = 0, ny = 0) { zoomAt(s.zoom * f, nx, ny); },
    /** Zoom slider: set an absolute zoom (1 to 4), centred on (nx, ny). */
    zoomTo(z, nx = 0, ny = 0) { zoomAt(z, nx, ny); },
    get spinning() { return s.spin; },
    /** The 3D piece itself (three.js group), for exporting. */
    model() { return engine.model ? engine.model() : null; },
    /** Current [turn, tilt] angle, in the same order as CJ3D.VIEWS entries. */
    get angle() { return [s.ry, s.rx]; },
    spin(on) { s.spin = on; s.idle = 0; },
    /** Device motion: tilting the phone turns the piece (starting from the current angle). */
    tilt(on) {
      if (s.tiltFn) { window.removeEventListener('deviceorientation', s.tiltFn); s.tiltFn = null; }
      if (!on) return;
      let base = null;
      s.tiltFn = (e) => { if (e.beta == null || e.gamma == null) return;
        if (!base) base = { b: e.beta, g: e.gamma, ry: s.ry, rx: s.rx };
        s.spin = false; s.vx = s.vy = 0;
        s.ty = base.ry + (e.gamma - base.g) * Math.PI / 90;            // left/right tilt turns it round
        s.tx = Math.max(-1.4, Math.min(1.5, base.rx + (e.beta - base.b) * Math.PI / 150)); // forward/back tilts it
        s.idle = performance.now(); };
      window.addEventListener('deviceorientation', s.tiltFn);
    },
    /** Returns a square JPEG of what the viewer shows right now. */
    snapshot(size = 480, bg = '#F4F5F7', view = null) {
      const keep = [s.zoom, s.cx, s.cy, s.ry, s.rx], v = view && X.VIEWS[s.kind] && X.VIEWS[s.kind][view];
      s.zoom = 1; s.cx = 0; s.cy = 0;
      if (v) { s.ry = v[0]; s.rx = v[1]; }
      const camKeep = Object.assign({}, cam); draw();
      [s.zoom, s.cx, s.cy, s.ry, s.rx] = keep; Object.assign(cam, camKeep);
      const out = document.createElement('canvas');
      out.width = out.height = size;
      const x = out.getContext('2d'), w = canvas.width, h = canvas.height, side = Math.min(w, h);
      x.fillStyle = bg; x.fillRect(0, 0, size, size);
      x.drawImage(canvas, (w - side) / 2, (h - side) / 2, side, side, 0, 0, size, size);
      return out.toDataURL('image/jpeg', 0.88);
    },
  };
  return ctl;
}

/** Fills <img data-cj-render='{"kind":..,"c":{..},"view":..,"bg":"#hex"}'> with a cached still once it nears the viewport. */
function hydrateStills(root) {
  const imgs = root.querySelectorAll('img[data-cj-render]:not([data-cj-done])');
  if (!imgs.length) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      const img = entry.target;
      const spec = JSON.parse(img.getAttribute('data-cj-render'));
      spec.c = Object.assign({}, DEFAULTS, spec.c);
      window.CJ3D.render(spec, (url) => {
        if (!url) return;
        img.src = url;
        img.closest('.cj-frame')?.classList.add('is-ready');
      });
    });
  }, { rootMargin: '300px' });
  imgs.forEach((img) => { img.setAttribute('data-cj-done', ''); io.observe(img); });
}

/** Mounts <canvas data-cj-live='{"kind":..,"c":{..}}'> viewers (hero sections). */
function hydrateLive(root) {
  root.querySelectorAll('canvas[data-cj-live]:not([data-cj-done])').forEach((canvas) => {
    canvas.setAttribute('data-cj-done', '');
    try {
      mountViewer(canvas, JSON.parse(canvas.getAttribute('data-cj-live')));
      canvas.closest('.cj-frame')?.classList.add('is-ready');
    } catch (e) {
      canvas.closest('.cj-frame')?.classList.add('is-ready', 'is-failed');
    }
  });
}

function hydrate(root = document) {
  window.CJReady.then(() => {
    hydrateStills(root);
    hydrateLive(root);
    window.CJ3D.pump();
  });
}

window.CJView = { mountViewer, DEFAULTS, hydrate };
hydrate();
document.addEventListener('shopify:section:load', (e) => hydrate(e.target));
